import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Flex, Text, Button, Stack, Title } from "@mantine/core";
import { PageContainer } from "../components/Container/PageContainer";
import { dbService } from "../firebaseInstance";
import { useUserDispatch, UserTypes, useUser } from "../contexts/UserContext";
import { UserInfoSummarySection } from "../template/account/UserInfoSummarySection";
import other from "../designTokens/other.esm";
import { useLogOut } from "../utils/auth";
import { useThreadsState } from '../contexts/ThreadsContext';
import DataLoader from "../components/DataLoader";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "../hooks/useIsMobile";
import { DeleteAccountModal } from "../components/modal/DeleteAccountModal";
import { AvatarSyncButton } from "../components/AvatarSyncButton/AvatarSyncButton";

const MyAccount = ({ firstThreadDate }) => {
	const { t } = useTranslation();
	const { profile, userLoading, error } = useUser();
	const isMobile = useIsMobile('xl');
	const navigate = useNavigate();
	const dispatch = useUserDispatch();
	const logOut = useLogOut();
	const { lifetimeInsight, isLoading } = useThreadsState();
	const [deleteModalOpened, setDeleteModalOpened] = useState(false);

	const handleDeleteAccount = async () => {
		try {
			const userId = localStorage.getItem("userID");
			console.log(userId);

			const userDocRef = dbService.collection("user").doc(userId);

			// Function to delete all documents in a subcollection
			const deleteSubcollection = async (subcollectionName) => {
				const subcollectionRef = userDocRef.collection(subcollectionName);
				const snapshot = await subcollectionRef.get();
				
				// Delete in batches of 500 (Firestore batch limit)
				const batchSize = 500;
				const batches = [];
				
				for (let i = 0; i < snapshot.docs.length; i += batchSize) {
					const batch = dbService.batch();
					const batchDocs = snapshot.docs.slice(i, i + batchSize);
					
					batchDocs.forEach((doc) => {
						batch.delete(doc.ref);
					});
					
					batches.push(batch.commit());
				}
				
				await Promise.all(batches);
			};

			// Delete all known subcollections
			await deleteSubcollection("threads");
			await deleteSubcollection("insight");
			await deleteSubcollection("scheduledPosts"); // 스케줄된 포스트가 있다면
			
			// Finally delete the user document itself
			await userDocRef.delete();

			// Clear local storage and context after successful deletion
			localStorage.removeItem("accessToken");
			localStorage.removeItem("userID");
			dispatch({
				type: UserTypes.CLEAR_PROFILE,
			});
			localStorage.clear();
			console.log("go to home page");
			setDeleteModalOpened(false);
			navigate("/");
		} catch (error) {
			console.error("Error deleting account:", error);
			alert(t("account.deleteAccount.failed"));
		}
	};

	return (
		<PageContainer>
			<DataLoader
				msg={t("loading.lifetime")}
				isLoad={isLoading}
			/>
			{!isLoading && (
				<>
					{profile !== null && lifetimeInsight !== null && (
						<UserInfoSummarySection
							profile={profile}
							insight={lifetimeInsight}
							postLoading={isLoading}
							isMobile={isMobile}
							firstThreadDate={firstThreadDate}
						/>
					)}

					{/* Avatar Sync Section */}
					<Flex direction="column" gap={isMobile ? 12 : 16}>
						<Text size={isMobile ? "lg" : "xl"} fw={600}>
							프로필 이미지 관리
						</Text>
						<Flex maw={isMobile ? "100%" : 400}>
							<AvatarSyncButton 
								size={isMobile ? "sm" : "md"}
								variant="light"
								fullWidth
							/>
						</Flex>
					</Flex>

					{/* Account Actions Section */}
					<Flex direction="column" gap={isMobile ? 12 : 16}>
						<Text size={isMobile ? "lg" : "xl"} fw={600}>
							{t("account.actions")}
						</Text>
						<Flex direction="column" gap={8} maw={isMobile ? "100%" : 300}>
							<Button color="gray" variant="filled" onClick={logOut}>
								{t("account.logout")}
							</Button>
							<Button color="red" variant="filled" onClick={() => setDeleteModalOpened(true)}>
								{t("account.deleteAccount.button")}
							</Button>
						</Flex>
					</Flex>

					<DeleteAccountModal
						opened={deleteModalOpened}
						onClose={() => setDeleteModalOpened(false)}
						onConfirm={handleDeleteAccount}
						userProfile={profile}
					/>
				</>
			)}
		</PageContainer>
	);
};

export default MyAccount;
